import { ConversationContextModal } from '@content-search/components/ConversationContextModal';
import { ErrorDisplay } from '@core/components/ErrorDisplay';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import { AuditDecisionPanel } from '@internal-audit/components/AuditDecisionPanel';
import { AuditItemSection } from '@internal-audit/components/AuditItemSection';
import {
  type AuditItemFilters,
  type AuditItemPagination,
  type PageSize,
  createEmptyAuditItemFilters,
  createDefaultAuditItemPagination,
  extractFilterOptionsFromItems,
} from '@internal-audit/components/AuditItemsFilters';
import { AuditSessionControlBar } from '@internal-audit/components/AuditSessionControlBar';
import { CompleteAuditDialog } from '@internal-audit/components/CompleteAuditDialog';
import type { GraphQLAuditItem, GraphQLAuditDecision, FlexibleAuditSession, GraphQLCommunity } from '@internal-audit/domain/graphql-types';
import { useCompleteAuditSession } from '@internal-audit/hooks/useCompleteAuditSession';
import { useConversationContextMenu } from '@internal-audit/hooks/useConversationContextMenu';
import { useGetAuditDecisions } from '@internal-audit/hooks/useGetAuditDecisions';
import { useGetAuditItemFilterOptions } from '@internal-audit/hooks/useGetAuditItemFilterOptions';
import { useGetAuditItems } from '@internal-audit/hooks/useGetAuditItems';
import { useAuditFiltersScrollPosition } from '@internal-audit/hooks/useScrollPosition';
import { useState, useMemo, useCallback, useEffect } from 'react';

interface AuditItemsTabProps {
  communityId: string;
  sessionId: string;
  community: GraphQLCommunity;
  session: FlexibleAuditSession;
  onCompleteAudit?: () => void;
  onDeleteAuditSession?: () => void;
}

// eslint-disable-next-line complexity, max-statements
export function AuditItemsTab({
  communityId,
  sessionId,
  community: _community,
  session,
  onCompleteAudit,
  onDeleteAuditSession: _onDeleteAuditSession,
}: AuditItemsTabProps) {
  // Get audit items and decisions data using the new 4-query architecture
  const {
    items,
    itemsResponse,
    loading: itemsLoading,
    error: itemsError,
    refetch,
  } = useGetAuditItems(sessionId, {
    pageSize: 100, // Default to 100 to match operation default
  });
  const { decisions, loading: decisionsLoading, error: decisionsError } = useGetAuditDecisions(sessionId);

  // Combine loading and error states
  const loading = itemsLoading || decisionsLoading;
  const error = itemsError || decisionsError;

  // Complete audit session hook
  const { completeAuditSession } = useCompleteAuditSession();

  // Scroll position for control bar visibility
  const { isTargetOutOfView } = useAuditFiltersScrollPosition();

  // Filter and pagination state - now for server-side pagination
  const [filters, setFilters] = useState<AuditItemFilters>(createEmptyAuditItemFilters());
  const [pagination, setPagination] = useState<AuditItemPagination>(() => ({
    ...createDefaultAuditItemPagination(),
    pageSize: 100, // Match the default from the hook
    currentPage: itemsResponse?.page || 1,
  }));
  const [paginationLoading, setPaginationLoading] = useState(false);

  // Decision panel state - use GraphQL item type directly
  const [selectedItem, setSelectedItem] = useState<GraphQLAuditItem | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [panelLoading, setPanelLoading] = useState(false);

  // Conversation Context Modal
  const { mediaServiceUrl } = useSystemConfig();
  const conversationContextMenu = useConversationContextMenu({ communityId });

  // Complete audit dialog state
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);

  // Error dialog state
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Check if the audit session is completed
  const isSessionCompleted = useMemo(() => session?.completedAt !== null && session?.completedAt !== undefined, [session?.completedAt]);

  // Panel management functions
  const handleItemSelect = useCallback(
    (item: GraphQLAuditItem) => {
      if (isSessionCompleted) {
        return; // Don't allow selection for completed sessions
      }

      setSelectedItem(item);
      setIsPanelOpen(true);
    },
    [isSessionCompleted]
  );

  const handlePanelClose = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedItem(null);
  }, []);

  const handleDecisionSave = useCallback((_decision: GraphQLAuditDecision) => {
    // The cache will be updated automatically by the hook
  }, []);

  // Find current user's decision for selected item using decisions from cache
  const currentUserDecision = useMemo(() => {
    if (!selectedItem || !decisions) return undefined;

    // Find decisions for this specific item
    const itemDecisions = decisions.filter(decision => decision.auditItemId === selectedItem.id);

    // Look for the current user's decision
    const decision = itemDecisions.find(decision => decision.isCurrentUser);

    // Return decision directly from GraphQL - no artificial transformation needed
    return decision;
  }, [selectedItem, decisions]);

  // Use server-side filter options to ensure stable filter dropdowns
  // Filter options are based on complete dataset, not filtered results
  const { data: filterOptionsData } = useGetAuditItemFilterOptions(communityId || '', sessionId || '');

  const filterOptions = useMemo(() => {
    if (!filterOptionsData?.community?.auditSession?.auditItemFilterOptions) {
      // Fallback to client-side extraction during loading or error
      return extractFilterOptionsFromItems(items as GraphQLAuditItem[]);
    }

    const serverOptions = filterOptionsData.community.auditSession.auditItemFilterOptions;

    return {
      availableLabels: serverOptions.labels,
      availableSeverities: serverOptions.severityLevels,
      availableViolatesPolicyOptions: serverOptions.violatesPolicyOptions ?? [],
    };
  }, [filterOptionsData, items]);

  // Extract decisions data for the decisions map using separate decisions data
  const rawItemsWithDecisions = useMemo(() => {
    if (!decisions) return [];

    return items.map(item => ({
      id: item.id,
      decisions: decisions.filter(decision => decision.auditItemId === item.id),
    }));
  }, [items, decisions]);

  // Map frontend filters to GraphQL input parameters (BFF handles API mapping)
  const mapFiltersToGraphQLInput = useCallback((filters: AuditItemFilters) => {
    const input: Record<string, unknown> = {};

    // Pass filters directly to BFF - let BFF handle API parameter mapping
    if (filters.status) {
      input.status = filters.status;
    }

    if (filters.label) {
      input.label = filters.label;
    }

    if (filters.severity !== undefined) {
      input.severity = filters.severity;
    }

    if (filters.multipleDecisionsStatus) {
      input.multipleDecisionsStatus = filters.multipleDecisionsStatus;
    }

    if (filters.violatesPolicy !== undefined) {
      input.violatesPolicy = filters.violatesPolicy;
    }

    return input;
  }, []);

  // All filtering is now done server-side through the API
  const paginatedItems = items;

  // Use server-side total count since all filtering is now done server-side
  const totalFilteredItems = itemsResponse?.totalCount || 0;

  // Handle filter changes - trigger server refetch with filter parameters
  const handleFiltersChange = useCallback(
    (newFilters: AuditItemFilters) => {
      setFilters(newFilters);
      // Show shimmer loading state for data list only (not filter bar or entire section)
      setPaginationLoading(true);

      // Map filters to GraphQL input (BFF handles API mapping)
      const filterParams = mapFiltersToGraphQLInput(newFilters);

      // Refetch with filter parameters and reset to first page
      refetch({
        page: 1,
        pageSize: pagination.pageSize,
        ...filterParams,
      })
        .then(() => {
          // Reset pagination to first page when filters change
          setPagination(prev => ({ ...prev, currentPage: 1 }));
        })
        .finally(() => {
          setPaginationLoading(false);
        });
    },
    [mapFiltersToGraphQLInput, pagination.pageSize, refetch]
  );

  // Update pagination state when itemsResponse changes
  useEffect(() => {
    if (itemsResponse) {
      setPagination(prev => ({
        ...prev,
        currentPage: itemsResponse.page,
        pageSize: itemsResponse.pageSize as PageSize,
      }));
    }
  }, [itemsResponse]);

  // Show error dialog when there's an error
  useEffect(() => {
    if (error) {
      setShowErrorDialog(true);
    }
  }, [error]);

  // Handle error dialog close
  const handleCloseErrorDialog = useCallback(() => {
    setShowErrorDialog(false);
  }, []);

  // Handle error retry
  const handleErrorRetry = useCallback(() => {
    setShowErrorDialog(false);
    window.location.reload();
  }, []);

  // Handle complete audit action
  const handleCompleteAudit = () => {
    setIsCompleteDialogOpen(true);
  };

  // Handle complete audit confirmation
  const handleCompleteAuditConfirm = async () => {
    if (!session?.id) return;

    try {
      const result = await completeAuditSession(session.id);

      if (result.success) {
        setIsCompleteDialogOpen(false);
        onCompleteAudit?.();
      } else {
        // Error handling is done within the hook
      }
    } catch {
      // Error handling is done within the hook
    }
  };

  // Handle complete audit dialog close
  const handleCompleteDialogClose = () => {
    setIsCompleteDialogOpen(false);
  };

  return (
    <>
      {/* Sticky Audit Session Control Bar */}
      {session && (
        <AuditSessionControlBar
          filteredItemsCount={totalFilteredItems}
          isLoading={loading}
          isVisible={isTargetOutOfView && !loading}
          session={session as unknown as FlexibleAuditSession}
          onCompleteAudit={handleCompleteAudit}
        />
      )}

      {/* Audit Items Section with integrated filters */}
      <AuditItemSection
        filterOptions={filterOptions}
        filters={filters}
        isDataLoading={paginationLoading} // Only show data list shimmer during filter/pagination changes
        isLoading={loading} // Show full loading state during initial load
        isSessionCompleted={isSessionCompleted}
        items={paginatedItems as unknown as GraphQLAuditItem[]}
        pagination={pagination}
        panelLoading={panelLoading}
        rawItemsWithDecisions={rawItemsWithDecisions}
        selectedItemId={selectedItem?.id}
        session={session as unknown as FlexibleAuditSession}
        totalItemsCount={totalFilteredItems}
        onContext={conversationContextMenu.handleOpenContextModal}
        onFiltersChange={handleFiltersChange}
        onItemSelect={handleItemSelect}
        onPaginationChange={newPagination => {
          setPagination(newPagination);
          setPaginationLoading(true);

          // Refetch with new pagination parameters and current filters
          const filterParams = mapFiltersToGraphQLInput(filters);

          refetch({
            page: newPagination.currentPage,
            pageSize: newPagination.pageSize,
            ...filterParams,
          }).finally(() => {
            setPaginationLoading(false);
            // Scroll to top of the list when changing pages for better UX
            window.scrollTo(0, 0);
          });
        }}
      />

      {/* Fixed Side Panel for Decision Form */}
      {isPanelOpen && (
        <AuditDecisionPanel
          communityId={communityId || ''}
          currentUserDecision={currentUserDecision}
          isControlBarVisible={isTargetOutOfView && !loading}
          isSessionCompleted={isSessionCompleted}
          selectedItem={selectedItem}
          onClose={handlePanelClose}
          onLoadingChange={setPanelLoading}
          onSave={handleDecisionSave}
        />
      )}

      {/* Complete Audit Dialog */}
      <CompleteAuditDialog
        auditSession={session as unknown as FlexibleAuditSession}
        isOpen={isCompleteDialogOpen}
        onClose={handleCompleteDialogClose}
        onConfirm={handleCompleteAuditConfirm}
      />

      {/* Error Dialog */}
      <ErrorDisplay
        correlationId={(error as { correlationId?: string })?.correlationId}
        isOpen={showErrorDialog}
        message={
          (error as { message?: string; userMessage?: string })?.message ||
          (error as { message?: string; userMessage?: string })?.userMessage ||
          'Failed to load audit session details.'
        }
        title="Error Loading Session"
        onClose={handleCloseErrorDialog}
        onRetry={handleErrorRetry}
      />

      {/* Conversation Context Modal */}
      {conversationContextMenu.isContextModalOpen && (
        <ConversationContextModal
          communityId={conversationContextMenu.contextModalProps.communityId}
          contentSearchErrors={conversationContextMenu.contextModalProps.contentSearchErrors}
          isOpen={conversationContextMenu.isContextModalOpen}
          isSearchingContent={conversationContextMenu.contextModalProps.isSearchingContent}
          mediaServiceUrl={mediaServiceUrl}
          metadata={conversationContextMenu.contextModalProps.metadata}
          selectedMessage={conversationContextMenu.contextModalProps.selectedMessage}
          subjectId={conversationContextMenu.contextModalProps.subjectId}
          topicName={conversationContextMenu.contextModalProps.topicName}
          onClose={conversationContextMenu.handleCloseContextModal}
        />
      )}
    </>
  );
}
